import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, merge, type Observable } from 'rxjs';
import { EditLinks } from '../../../../@core/constants/edit-links';
import { DeletionOrReductionResponse, DocumentLineItem, DocumentStatusType } from '../../../../generated/generated-types';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import { DocumentService } from '../../services/document.service';
import { PackingConfigWidgetComponent } from '../packing-config-widget/packing-config-widget.component';
import { WidgetEditGenericDocumentComponent } from '../widget-edit-generic-document/widget-edit-generic-document.component';
@Component({
  selector: 'app-formly-packing-config-widget',
  templateUrl: './formly-packing-config-widget.component.html',
  styleUrl: './formly-packing-config-widget.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [
    LocaleDatePipe,
  ],
  imports: [
    CommonModule,
    PackingConfigWidgetComponent,
    ButtonModule,
    TranslatePipe,
    BlockableDiv,
    TooltipModule
  ]
})
export class FormlyPackingConfigWidgetComponent extends FieldType implements AfterViewInit {

  packingConfigWidget = viewChild<PackingConfigWidgetComponent>('packingConfigWidget');

  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private documentService = inject(DocumentService);

  ngAfterViewInit(): void {
    const detail = this.formState?.detailComponent as WidgetEditGenericDocumentComponent | undefined;
    const sources: Observable<unknown>[] = [];
    const lineItemsControl = this.form?.controls?.['lineItems'];
    if (lineItemsControl?.valueChanges) {
      sources.push(lineItemsControl.valueChanges);
    }
    if (detail?.packingConfigLineItemsChanged$) {
      sources.push(detail.packingConfigLineItemsChanged$);
    }
    if (sources.length === 0) {
      return;
    }
    merge(...sources).pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      // Pack save already merged package quantities and refreshed document sums.
      // The positions-list refetch that follows must not load every line and every package again.
      // A line-item edit elsewhere still needs the full packing reload.
      if (this.packingConfigWidget()?.consumeSkipNextLineItemDrivenReload()) {
        return;
      }
      this.packingConfigWidget()?.refreshPackingFromServer();
      this.refetchDocumentStatusInBackground();
    });
  }

  private refetchDocumentStatusInBackground(): void {
    const documentId = this.model?.id;
    if (!documentId) {
      return;
    }
    void this.documentService.refetchOnlyDocumentLineItemCausedChanges(documentId);
  }


  onSavedPackagingDLI(savedDli: DocumentLineItem) {
    //TODO: find more performant way - similar to below - but it doesnt work - just with refresh
    // this.formState?.dliListComponent?.addEditLineItemWidget()?.itemsSig?.update((dlis: DocumentLineItem[]) => {
    //   const existingDli = dlis.find(dli => dli.productId === savedDli.productId);
    //   if (existingDli) {
    //     existingDli.quantity += savedDli.quantity;
    //   } else {
    //     dlis.push(savedDli);
    //   }
    //   return dlis;
    // });
    this.formState?.dliListComponent?.addEditLineItemWidget()?.refresh();
    this.refetchDocumentStatusInBackground();
  }

  onSavedPacking() {
    // Header sums are applied in the packing widget. Do not refetch every line item here.
  }

  onDeletedPackagingDLI(delRes: DeletionOrReductionResponse) {
    //TODO: find more performant way - similar to below - but it doesnt work - just with refresh

    // this.formState?.dliListComponent?.addEditLineItemWidget()?.itemsSig?.update((dlis: DocumentLineItem[]) => {
    //   // Early return if no valid response
    //   if (!delRes?.modelId) {
    //     return dlis;
    //   }

    //   // Find the item to update
    //   const itemToUpdate = dlis.find(dli => dli.id === delRes.modelId);

    //   if (!itemToUpdate) {
    //     return dlis;
    //   }

    //   // Update quantity
    //   itemToUpdate.quantity = delRes.newQuantity ?? 0;

    //   // Remove item if quantity is 0, otherwise return updated list
    //   return itemToUpdate.quantity === 0
    //     ? dlis.filter(dli => dli.id !== itemToUpdate.id)
    //     : dlis;
    // });
    this.formState?.dliListComponent?.addEditLineItemWidget()?.refresh();
    this.refetchDocumentStatusInBackground();
  }

  onDeletedPacking() {
    // Header sums are applied in the packing widget.
  }

  async changeToFollowUpDocument() {
    if (this.formState?.detailComponent?.postDocumentLinks()?.[0]?.['targetDocument']?.id) {
      this.router.navigate([`${EditLinks.DOCUMENT}${this.formState?.detailComponent?.postDocumentLinks()[0]['targetDocument'].id}`]);
    }
  }

  //TODO: computed signal
  disabledCheck() {
    if (this.model.status == DocumentStatusType.CO || this.model.status == DocumentStatusType.RE) {
      let nextDocAllowPacking = this.formState?.detailComponent?.postDocumentLinks()?.[0]?.['targetDocument']?.documentDefinition?.allowPacking;
      if (nextDocAllowPacking) {
        return true;
      }
      return false;
    }
    return false;
  }

  onCompletePackingAndTransferDocument() {
    if (!this.formState?.detailComponent) {
      console.warn('formState.detailComponent is null in onCompletePackingAndTransferDocument');
      return;
    }
    if (!this.formState.detailComponent.widgetDocumentRelations) {
      console.warn('widgetDocumentRelations is null in onCompletePackingAndTransferDocument');
    } else {
      this.formState.detailComponent.widgetDocumentRelations.refresh();
    }
    this.formState.detailComponent.needsDliWidgetRefresh$.next(true);
    this.refetchDocumentStatusInBackground();
  }
}
